import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getLang, setLang as persistLang, applyDocumentLang, setCurrentLang, type Lang } from '../i18n';
import { t as translate, formatDate as fmtDate, formatNumber as fmtNum, formatCurrency as fmtCur } from '../i18n';

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
  formatDate: (date: Date | string, format?: 'date' | 'time' | 'datetime') => string;
  formatNumber: (num: number) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const initial = getLang();
    setCurrentLang(initial);
    return initial;
  });

  // Apply document direction on mount and language change
  useEffect(() => {
    applyDocumentLang(lang);
    setCurrentLang(lang);
  }, [lang]);

  const setLang = (newLang: Lang) => {
    persistLang(newLang);
    setCurrentLang(newLang);
    setLangState(newLang);
    applyDocumentLang(newLang);
  };

  const isRTL = lang === 'ar' || lang === 'he';

  const value: LanguageContextType = {
    lang,
    setLang,
    t: (key: string) => translate(key, lang),
    formatDate: (date: Date | string, format?: 'date' | 'time' | 'datetime') => fmtDate(date, format, lang),
    formatNumber: (num: number) => fmtNum(num, lang),
    formatCurrency: (amount: number, currency?: string) => fmtCur(amount, currency, lang),
    isRTL,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Also export a simple hook that doesn't require context (for rare cases)
export function useSimpleTranslation() {
  const [lang, setLangState] = useState<Lang>(getLang());

  useEffect(() => {
    const storedLang = getLang();
    setLangState(storedLang);
    setCurrentLang(storedLang);
  }, []);

  return {
    lang,
    t: (key: string) => translate(key, lang),
    isRTL: lang === 'ar' || lang === 'he',
  };
}
